import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Percent, Users, CreditCard, Store, CheckCircle2, AlertCircle } from "lucide-react";

const money = (n) => `$${(n ?? 0).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const statusColor = (s) => ({
  active: "text-volt", trialing: "text-volt", past_due: "text-epred",
  canceled: "text-epmuted", inactive: "text-epmuted",
}[s] || "text-epsub");

export default function Admin() {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/admin/overview"), api.get("/admin/subscribers")])
      .then(([ov, sb]) => {
        setData(ov.data);
        setSubs(sb.data.subscribers || []);
      })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const stats = [
    { icon: TrendingUp, label: "Gross Revenue", value: money(data?.gross_revenue), sub: "processed" },
    { icon: Percent, label: `Fees Collected (${data?.fee_percent ?? 2}%)`, value: money(data?.platform_fees_collected), sub: "your earnings", accent: true },
    { icon: CreditCard, label: "Transactions", value: data?.transactions ?? 0, sub: "paid" },
    { icon: Users, label: "Active Subscriptions", value: data?.active_subscriptions ?? 0, sub: `of ${data?.total_end_users ?? 0} users` },
  ];

  const m = data?.merchant || {};

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="overline mb-3 text-volt">Platform Admin</div>
        <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter mb-10">Control</h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => <div key={i} className="h-40 ep-loading" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5" data-testid="admin-stats">
              {stats.map((s, i) => (
                <div key={i} className={`ep-card p-6 ${s.accent ? "bg-volt text-black" : ""}`} data-testid={`admin-stat-${i}`}>
                  <s.icon size={22} strokeWidth={1.5} className={s.accent ? "text-black" : "text-volt"} />
                  <div className={`font-mono-accent text-xs uppercase tracking-[0.2em] font-bold mt-6 ${s.accent ? "text-black/70" : "text-epsub"}`}>{s.label}</div>
                  <div className="font-display text-4xl font-bold tracking-tighter mt-2">{s.value}</div>
                  <div className={`text-xs mt-1 ${s.accent ? "text-black/60" : "text-epmuted"}`}>{s.sub}</div>
                </div>
              ))}
            </div>

            <div className="ep-card p-8 mt-8" data-testid="admin-merchant-panel">
              <div className="flex items-center gap-3 mb-5">
                <Store size={20} className="text-volt" />
                <h2 className="font-display uppercase text-2xl font-semibold tracking-tight">Connected Merchant</h2>
              </div>
              {m.connected ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-epborder pb-2"><span className="text-epmuted font-mono-accent uppercase text-xs tracking-widest">Owner</span><span>{m.owner_email}</span></div>
                  <div className="flex justify-between border-b border-epborder pb-2"><span className="text-epmuted font-mono-accent uppercase text-xs tracking-widest">Account</span><span className="font-mono-accent">{m.account_id}</span></div>
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-epmuted font-mono-accent uppercase text-xs tracking-widest">Status</span>
                    <span className="flex items-center gap-2">
                      {m.charges_enabled ? <CheckCircle2 size={15} className="text-volt" /> : <AlertCircle size={15} className="text-yellow-400" />}
                      {m.charges_enabled ? "Active" : "Pending verification"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-epsub text-sm leading-relaxed">
                  No merchant connected yet. Once the app owner connects their bank via Stripe, their account and
                  payout status appear here. You'll automatically collect the {data?.fee_percent ?? 2}% fee on every
                  subscription payment.
                </p>
              )}
            </div>

            <div className="ep-card mt-8 overflow-hidden" data-testid="admin-subscribers-panel">
              <div className="flex items-center gap-3 p-8 pb-5">
                <Users size={20} className="text-volt" />
                <h2 className="font-display uppercase text-2xl font-semibold tracking-tight">Subscribers & Subscriptions</h2>
                <span className="overline ml-auto">{subs.length} total</span>
              </div>
              {subs.length === 0 ? (
                <p className="text-epsub text-sm px-8 pb-8">No end users have signed up yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="subscribers-table">
                    <thead>
                      <tr className="text-left border-y border-epborder bg-bg1">
                        {["Member", "Status", "Paid to date", "Payments", "Next due", "Amount due", "Joined"].map((h) => (
                          <th key={h} className="font-mono-accent uppercase text-[0.65rem] tracking-[0.15em] text-epmuted px-6 py-3 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {subs.map((s) => (
                        <tr key={s.id} className="border-b border-epborder hover:bg-bg1 transition-colors" data-testid={`subscriber-row-${s.id}`}>
                          <td className="px-6 py-4">
                            <div className="font-semibold text-white">{s.name || "—"}</div>
                            <div className="text-epmuted text-xs">{s.email}</div>
                          </td>
                          <td className={`px-6 py-4 font-mono-accent uppercase text-xs tracking-widest ${statusColor(s.status)}`}>{s.status}</td>
                          <td className="px-6 py-4 font-semibold">{money(s.total_paid)}</td>
                          <td className="px-6 py-4 text-epsub">{s.payments}</td>
                          <td className="px-6 py-4 text-epsub whitespace-nowrap">{fmtDate(s.next_due_date)}</td>
                          <td className="px-6 py-4 font-semibold text-volt">{s.amount_due > 0 ? money(s.amount_due) : "—"}</td>
                          <td className="px-6 py-4 text-epmuted whitespace-nowrap">{fmtDate(s.joined)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
